import React, { useState } from "react";
import {Phone, Mail, MapPin, Send} from 'lucide-react';
import toast, { Toaster } from "react-hot-toast";

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    vehicleInfo: "",
    racingClass: "",
    message: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Show success message
    toast.success(
      "Quote request submitted! We'll contact you within 24 hours.",
      {
        duration: 5000,
        style: {
          background: "#DC0000",
          color: "#fff",
        },
      }
    );

    // Reset form
    setFormData({
      name: "",
      email: "",
      phone: "",
      vehicleInfo: "",
      racingClass: "",
      message: "",
    });
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="bg-black min-h-screen">
      <Toaster position="top-center" />
      
      {/* Hero Section */}
      <section className="relative py-20 bg-[#1a1a1a] border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Get Your <span className="text-[#DC0000]">Free Quote</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8">
              Affordable drag car insurance quotes in minutes. Protect your
              high-value vehicle with specialized drag racers insurance.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Information */}
            <div className="lg:col-span-1">
              <h2 className="text-3xl font-bold mb-6">Get In Touch</h2>
              <p className="text-gray-400 mb-8">
                Ready to protect your drag racing investment? Contact us today
                for a personalized insurance quote.
              </p>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <Phone className="w-6 h-6 text-[#DC0000] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1">Phone</h3>
                    <a
                      href="tel:1-800-DRAG-CAR"
                      className="text-gray-400 hover:text-[#DC0000] transition-colors"
                    >
                      1-800-DRAG-CAR
                    </a>
                    <p className="text-sm text-gray-500 mt-1">
                      Mon-Fri: 8am-8pm EST
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <Mail className="w-6 h-6 text-[#DC0000] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1">Email</h3>
                    <a
                      href="mailto:info@dragcarinsurance.com"
                      className="text-gray-400 hover:text-[#DC0000] transition-colors"
                    >
                      info@dragcarinsurance.com
                    </a>
                    <p className="text-sm text-gray-500 mt-1">
                      24-48 hour response time
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <MapPin className="w-6 h-6 text-[#DC0000] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1">Coverage Area</h3>
                    <p className="text-gray-400">Nationwide Coverage</p>
                    <p className="text-sm text-gray-500 mt-1">
                      All 50 states available
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-12 p-6 bg-[#1a1a1a] rounded-lg border border-gray-800">
                <h3 className="text-xl font-bold mb-4">Why Choose Us?</h3>
                <ul className="space-y-3 text-gray-300 text-sm">
                  <li className="flex items-start">
                    <span className="text-[#DC0000] mr-2">✓</span>
                    Specialized in drag racing insurance
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#DC0000] mr-2">✓</span>
                    Fast quotes within 24 hours
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#DC0000] mr-2">✓</span>
                    Competitive rates for racers
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#DC0000] mr-2">✓</span>
                    Agreed value policies
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#DC0000] mr-2">✓</span>
                    24/7 claims support
                  </li>
                </ul>
              </div>
            </div>

            {/* Quote Form */}
            <div className="lg:col-span-2">
              <div className="bg-[#1a1a1a] p-8 rounded-lg border border-gray-800">
                <h2 className="text-3xl font-bold mb-6">Request Your Quote</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm font-medium mb-2"
                      >
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-black border border-gray-800 rounded-md focus:outline-none focus:border-[#DC0000] transition-colors"
                        placeholder="John Doe"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium mb-2"
                      >
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-black border border-gray-800 rounded-md focus:outline-none focus:border-[#DC0000] transition-colors"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label
                        htmlFor="phone"
                        className="block text-sm font-medium mb-2"
                      >
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-black border border-gray-800 rounded-md focus:outline-none focus:border-[#DC0000] transition-colors"
                        placeholder="(555) 123-4567"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="racingClass"
                        className="block text-sm font-medium mb-2"
                      >
                        Racing Class
                      </label>
                      <select
                        id="racingClass"
                        name="racingClass"
                        value={formData.racingClass}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-black border border-gray-800 rounded-md focus:outline-none focus:border-[#DC0000] transition-colors"
                      >
                        <option value="">Select a class</option>
                        <option value="bracket">Bracket Racing</option>
                        <option value="street">Street Class</option>
                        <option value="sportsman">Sportsman</option>
                        <option value="prostock">Pro Stock</option>
                        <option value="promod">Pro Mod</option>
                        <option value="topfuel">Top Fuel</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="vehicleInfo"
                      className="block text-sm font-medium mb-2"
                    >
                      Vehicle Information
                    </label>
                    <input
                      type="text"
                      id="vehicleInfo"
                      name="vehicleInfo"
                      value={formData.vehicleInfo}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-black border border-gray-800 rounded-md focus:outline-none focus:border-[#DC0000] transition-colors"
                      placeholder="e.g., 1969 Camaro, Modified Pro Street"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium mb-2"
                    >
                      Additional Details
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      className="w-full px-4 py-3 bg-black border border-gray-800 rounded-md focus:outline-none focus:border-[#DC0000] transition-colors resize-none"
                      placeholder="Tell us about your vehicle, modifications, racing experience, and what coverage you're looking for..."
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-[#DC0000] text-white px-8 py-4 rounded-md font-semibold text-lg hover:bg-red-700 transition-all transform hover:scale-105 flex items-center justify-center space-x-2"
                  >
                    <span>Submit Quote Request</span>
                    <Send className="w-5 h-5" />
                  </button>

                  <p className="text-sm text-gray-500 text-center">
                    By submitting this form, you agree to be contacted regarding
                    your drag car insurance quote. We respect your privacy and
                    will never share your information.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="py-16 bg-[#1a1a1a] border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-[#DC0000] mb-2">
                &lt;24hrs
              </div>
              <div className="text-gray-400">Average Quote Time</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-[#DC0000] mb-2">95%</div>
              <div className="text-gray-400">Customer Satisfaction</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-[#DC0000] mb-2">
                $800+
              </div>
              <div className="text-gray-400">Starting From</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-[#DC0000] mb-2">50</div>
              <div className="text-gray-400">States Covered</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
